import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AnalyticsListener } from "@/components/AnalyticsListener";
import { Toaster } from "sonner";
import SmoothScroll from "@/components/SmoothScroll";
import HomePage from "@/pages/HomePage";
import FloatingActions from "@/components/FloatingActions";

const AdminPage = lazy(() => import("@/pages/AdminPage"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const AreasIndex = lazy(() => import("@/pages/AreasIndex"));
const CarKeysIndex = lazy(() => import("@/pages/CarKeys").then((m) => ({ default: m.CarKeysIndex })));
const CarKeyAreaPage = lazy(() => import("@/pages/CarKeys").then((m) => ({ default: m.CarKeyAreaPage })));
const VehicleRemapIndex = lazy(() => import("@/pages/VehicleRemapIndex"));
const VehicleMakePage = lazy(() => import("@/pages/VehicleMakePage"));
const VehicleRemapPage = lazy(() => import("@/pages/VehicleRemapPage"));
const ServiceCityPage = lazy(() => import("@/pages/ServiceCityPage"));
const BookPage = lazy(() => import("@/pages/BookPage"));
const PricesPage = lazy(() => import("@/pages/PricesPage"));
const RemappingNearMe = lazy(() => import("@/pages/RemappingNearMe"));
const EastMidlandsHub = lazy(() => import("@/pages/EastMidlandsHub"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Pre-rendered SEO snapshots are reachable at /<route>.html; once React
// mounts, move to the clean URL so the router matches and canonicals hold.
function HtmlExtensionRedirect() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname.endsWith(".html")) {
      const clean = pathname.slice(0, -5) || "/";
      navigate(clean === "/index" ? "/" : clean + search + hash, { replace: true });
    }
  }, [pathname, search, hash, navigate]);

  // The homepage FAQPage JSON-LD (#faq-jsonld) is baked into the shared
  // index.html snapshot and is served as the SPA shell on every route. It
  // belongs to the homepage only — strip it on any non-home route so pages
  // with their own schema (services, vehicles, cities) don't carry a stray
  // duplicate FAQPage. Faq.jsx re-adds it when the homepage mounts.
  useEffect(() => {
    const home = pathname === "/" || pathname === "/index.html";
    if (!home) document.getElementById("faq-jsonld")?.remove();
  }, [pathname]);

  return null;
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="app-root">
      <div className="noise-overlay" aria-hidden="true" />
      <BrowserRouter>
        <HtmlExtensionRedirect />
        <AnalyticsListener />
        <SmoothScroll>
          <Suspense fallback={<div className="min-h-screen bg-carbon" />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/services/:slug/:city" element={<ServiceCityPage />} />
              <Route path="/book" element={<BookPage />} />
              <Route path="/prices" element={<PricesPage />} />
              <Route path="/remapping-near-me" element={<RemappingNearMe />} />
              <Route path="/areas/east-midlands" element={<EastMidlandsHub />} />
              <Route path="/areas" element={<AreasIndex />} />
              <Route path="/car-remapping/:slug" element={<CityPage />} />
              <Route path="/car-keys" element={<CarKeysIndex />} />
              <Route path="/car-keys/:slug" element={<CarKeyAreaPage />} />
              <Route path="/remap" element={<VehicleRemapIndex />} />
              <Route path="/remap/:make" element={<VehicleMakePage />} />
              <Route path="/remap/:vehicle/:city" element={<VehicleRemapPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <FloatingActions />
        </SmoothScroll>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position={window.matchMedia("(max-width: 767px)").matches ? "top-center" : "bottom-right"}
        offset={100}
        toastOptions={{
          style: {
            background: "#0F0F0F",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "0",
            color: "#fff",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
